import React, { useEffect, useRef, useState } from 'react';
import {
    BaseSystemPlugin,
    HOTKEY_TYPE,
    useFramework,
    useIdentity,
    useScale,
    useStagePadding,
    useStageScrollPosition,
    useStageUnit,
    useViewHeight,
    useViewWidth,
} from '@/framework';
import { getLayerInfoWithRotate } from '@/utils/layer-kit';
import { ILayerProps, RULER_BAR_SIZE } from '@/version-types';
import { drawHorizontalRuler, drawVerticalRuler } from '../operations/draw-rules';
import { setShowRulerBar } from '../operations/set-show-ruler-bar';
import { useRulerState } from '../states/ruler-state';
import type { RulerUnit } from '../types';
import UnitButton from './unit-button';
import './index.less';

const RulerBar: React.FC = () => {
    const scale = useScale();
    const identity = useIdentity();
    const framework = useFramework();
    const viewWidth = useViewWidth();
    const viewHeight = useViewHeight();
    const stageScrollPosition = useStageScrollPosition();
    const showRulerBar = useRulerState()((state) => state.showRulerBar);
    const rulerUnit = useRulerState()((state) => state.rulerUnit);
    const setRulerUnit = useRulerState()((state) => state.setRulerUnit);
    const stageUnit = useStageUnit();

    const horizontalRulerRef = useRef<HTMLCanvasElement>(null);
    const verticalRulerRef = useRef<HTMLCanvasElement>(null);

    const { paddingLeft, paddingTop } = useStagePadding();

    const [activeLayer, setActiveInfo] = useState<ILayerProps | null>(framework.operate.getActiveLayer());
    // 监听图层变化
    useEffect(() => {
        if (activeLayer?.id) {
            const handler = framework.events.onLayerChange(() => {
                const layer = framework.operate.getActiveLayer();
                setActiveInfo(layer);
            }, activeLayer.id);
            return () => framework.events.off(handler);
        }
    }, [activeLayer?.id]);

    // 监听激活图层变化
    useEffect(() => {
        const handler = framework.events.onActiveLayerDataUpdate((activeLayer) => {
            setActiveInfo(activeLayer);
        });
        return () => framework.events.off(handler);
    }, []);

    // 键盘事件(显示/隐藏标尺)
    useEffect(() => {
        const handler = framework.events.on(HOTKEY_TYPE.toggleRulerBar, () => {
            setShowRulerBar(identity, !showRulerBar);
        });
        return () => framework.events.off(handler);
    }, [showRulerBar]);

    // 单向同步右侧面板的单位
    useEffect(() => {
        setRulerUnit(stageUnit as RulerUnit);
    }, [stageUnit]);

    useEffect(() => {
        if (showRulerBar) {
            drawHorizontalRuler({
                scale,
                rulerUnit,
                paddingLeft,
                scrollLeft: stageScrollPosition.left,
                viewWidth,
                stageSize: framework.stage.getSize(),
                horizontalRulerRef,
            });
        }
    }, [scale, rulerUnit, viewWidth, stageScrollPosition.left, showRulerBar, paddingLeft]);

    useEffect(() => {
        if (showRulerBar) {
            drawVerticalRuler({
                scale,
                rulerUnit,
                paddingTop,
                scrollTop: stageScrollPosition.top,
                viewHeight,
                stageSize: framework.stage.getSize(),
                verticalRulerRef,
            });
        }
    }, [scale, rulerUnit, viewHeight, stageScrollPosition.top, showRulerBar, paddingTop]);

    // 阻止右键菜单
    const preventContextMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    if (!showRulerBar) {
        return null;
    }

    const { width, height, left, top } = activeLayer
        ? getLayerInfoWithRotate(activeLayer)
        : { width: 0, height: 0, left: 0, top: 0 };

    return (
        <BaseSystemPlugin name="ruler-bar">
            <div className="l-ruler-unix">
                <UnitButton />
            </div>

            <div className="l-horizontal-ruler__container" onContextMenu={preventContextMenu}>
                {activeLayer && (
                    <div
                        className="l-active-shadow l-active-horizontal-shadow"
                        style={{
                            left: paddingLeft + left * scale - RULER_BAR_SIZE - stageScrollPosition.left,
                            width: width * scale,
                        }}
                    />
                )}
                <canvas
                    ref={horizontalRulerRef}
                    className="l-ruler-bar__canvas horizontal-ruler"
                    style={{ width: viewWidth - RULER_BAR_SIZE, height: RULER_BAR_SIZE }}
                />
            </div>

            <div className="l-vertical-ruler__container" onContextMenu={preventContextMenu}>
                {activeLayer && (
                    <div
                        className="l-active-shadow l-active-vertical-shadow"
                        style={{
                            top: paddingTop + top * scale - RULER_BAR_SIZE - stageScrollPosition.top,
                            height: height * scale,
                        }}
                    />
                )}
                <canvas
                    ref={verticalRulerRef}
                    className="l-ruler-bar__canvas vertical-ruler"
                    style={{ width: RULER_BAR_SIZE, height: viewHeight - RULER_BAR_SIZE }}
                />
            </div>
        </BaseSystemPlugin>
    );
};

export default React.memo(RulerBar);
